import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LegalDocDialog } from '@/components/legal/LegalDocDialog';
import { Capacitor } from "@capacitor/core";
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTheme } from "next-themes";
import { Mail, Lock, User, Globe, Sun, Moon, Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { CareOSLoader } from '@/components/ui/careos-loader';
import { ThemedLogo } from '@/components/ui/themed-logo';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { z } from 'zod';
import { track, setPersonProperties } from '@/lib/analytics';

// Key for storing pending invitation in sessionStorage
const PENDING_INVITE_KEY = 'careos_pending_invite';

export default function AuthPage() {
  const { t, i18n } = useTranslation();

  const emailSchema = z.string().email(t('auth.invalidEmail'));
  const passwordSchema = z.string().min(6, t('auth.passwordTooShort'));

  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, signInWithGoogle, signInWithApple, user } = useAuth();
  const enableAppleLogin = import.meta.env.VITE_ENABLE_APPLE_LOGIN === 'true';

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [legalDoc, setLegalDoc] = useState<{ slug: string; title: string } | null>(null);

  // Get return URL from query params or sessionStorage (for invite flow)
  const getReturnUrl = () => {
    const urlReturnTo = searchParams.get('returnTo');
    if (urlReturnTo) return urlReturnTo;

    // Check sessionStorage for pending invite
    const pendingInvite = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (pendingInvite) return `/invite/${pendingInvite}`;

    return '/app';
  };

  const returnTo = getReturnUrl();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true });
    }
  }, [user, returnTo, navigate]);

  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <CareOSLoader size="lg" />
      </div>
    );
  }

  const validateForm = (isSignUp: boolean) => {
    try {
      emailSchema.parse(email);
      passwordSchema.parse(password);

      if (isSignUp && password !== confirmPassword) {
        toast.error(t('auth.passwordsDoNotMatch'));
        return false;
      }

      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      }
      return false;
    }
  };

  // Audience inferred from pending invite (caregiver/company member) vs direct signup (owner-ish).
  const inferAudience = (): 'b2c' | 'b2b' | 'invitee' => {
    if (sessionStorage.getItem(PENDING_INVITE_KEY)) return 'invitee';
    if (sessionStorage.getItem('careos_pending_company_invite')) return 'invitee';
    return 'b2c';
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm(false)) return;

    setIsLoading(true);
    // Stamps the email on the (still anonymous) PostHog profile so pre-login
    // and abandoned attempts aren't identified only by a UUID.
    setPersonProperties({ email: email.trim().toLowerCase() });
    const { error } = await signIn(email, password);
    setIsLoading(false);

    if (error) {
      toast.error(t('auth.invalidCredentials'));
    } else {
      track('onboarding_step_completed', { step: 'login', step_index: 1, flow: 'signin', method: 'email', audience: inferAudience() });
      navigate(returnTo);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm(true)) return;

    if (!name.trim()) {
      toast.error(t('auth.nameRequired') || 'Nome é obrigatório');
      return;
    }

    if (!acceptedTerms) {
      toast.error(t('auth.mustAcceptTerms') || 'É necessário aceitar os Termos de Uso e a Política de Privacidade');
      return;
    }

    setIsLoading(true);
    const audience = inferAudience();
    setPersonProperties({ email: email.trim().toLowerCase(), name: name.trim() });
    track('signup_started', { method: 'email', audience });
    track('onboarding_step_completed', { step: 'signup_method', step_index: 1, flow: 'signup', method: 'email', audience });
    const { error } = await signUp(email, password, name);

    if (error) {
      setIsLoading(false);
      if (error.message.includes('already registered')) {
        toast.error(t('auth.emailAlreadyExists'));
      } else {
        toast.error(error.message);
      }
      return;
    }

    // Registrar aceite (não bloqueia o fluxo em caso de falha, apenas loga)
    try {
      await supabase.functions.invoke('record-acceptance', {
        body: { slugs: ['terms', 'privacy'], method: 'signup' },
      });
    } catch (err) {
      console.warn('[Auth] failed to record acceptance', err);
    }

    setIsLoading(false);
    track('onboarding_step_completed', { step: 'signup_data', step_index: 2, flow: 'signup', method: 'email', audience });
    toast.success(t('auth.checkEmailForCode'));
    navigate('/verify-otp', { state: { email: email.trim().toLowerCase(), returnTo, name: name.trim(), audience } });
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    const audience = inferAudience();
    track('signup_method_selected', { method: 'google', audience });
    track('onboarding_step_completed', { step: 'signup_method', step_index: 1, flow: 'oauth', method: 'google', audience });
    const { error } = await signInWithGoogle(returnTo);
    setIsLoading(false);

    if (error) {
      toast.error(error.message);
    }
  };

  const handleAppleSignIn = async () => {
    setIsLoading(true);
    const audience = inferAudience();
    track('signup_method_selected', { method: 'apple', audience });
    track('onboarding_step_completed', { step: 'signup_method', step_index: 1, flow: 'oauth', method: 'apple', audience });
    const { error } = await signInWithApple(returnTo);
    setIsLoading(false);

    if (error) {
      toast.error(error.message);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error(t('auth.invalidEmail'));
      return;
    }
    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setForgotLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      setForgotSent(true);
    }
  };

  const currentLanguage = i18n.language?.startsWith('pt') ? 'pt-BR' : 'en';
  const toggleLanguage = () => i18n.changeLanguage(currentLanguage === 'pt-BR' ? 'en' : 'pt-BR');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 pt-16">
      {/* Top Right Controls */}
      <div className={`absolute right-4 flex items-center gap-2 ${Capacitor.isNativePlatform() ? 'top-12' : 'top-4'}`}>

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="rounded-full"
        >
          <Sun className="h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <span className="sr-only">Toggle theme</span>
        </Button>

        {/* Language Switcher */}
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={toggleLanguage}
        >
          <Globe className="h-[1.2rem] w-[1.2rem]" />
          <span className="sr-only">Toggle language</span>
        </Button>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <ThemedLogo className="h-24 w-24" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t('auth.pageHeading') || 'Acesse sua conta'}</h1>
          <CardDescription>
            {t('auth.appDescription') || 'Sistema de gestão de cuidados domiciliares'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('auth.hidePassword') || 'Esconder senha' : t('auth.showPassword') || 'Mostrar senha'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 h-auto text-sm text-muted-foreground"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    {t('auth.forgotPassword')}
                  </Button>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t('common.loading') : t('auth.login')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">{t('auth.name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Seu nome completo"
                      className="pl-10"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('auth.hidePassword') || 'Esconder senha' : t('auth.showPassword') || 'Mostrar senha'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm">{t('auth.confirmPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="signup-confirm"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('auth.hidePassword') || 'Esconder senha' : t('auth.showPassword') || 'Mostrar senha'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-2 pt-1">
                  <Checkbox
                    id="signup-accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(v === true)}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="signup-accept-terms"
                    className="text-xs text-muted-foreground leading-snug cursor-pointer"
                  >
                    {t('auth.acceptTermsPrefix') || 'Li e concordo com os'}{' '}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalDoc({ slug: 'terms', title: t('auth.termsOfUse') || 'Termos de Uso' }); }}
                      className="text-primary underline underline-offset-2 hover:text-primary/80"
                    >
                      {t('auth.termsOfUse') || 'Termos de Uso'}
                    </button>{' '}
                    {t('common.and') || 'e'}{' '}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLegalDoc({ slug: 'privacy', title: t('auth.privacyPolicy') || 'Política de Privacidade' }); }}
                      className="text-primary underline underline-offset-2 hover:text-primary/80"
                    >
                      {t('auth.privacyPolicy') || 'Política de Privacidade'}
                    </button>
                    .
                  </Label>
                </div>
                <LegalDocDialog
                  slug={legalDoc?.slug ?? 'terms'}
                  key={legalDoc?.slug ?? 'none'}
                  fallbackTitle={legalDoc?.title ?? ''}
                  open={!!legalDoc}
                  onOpenChange={(o) => { if (!o) setLegalDoc(null); }}
                />

                <Button type="submit" className="w-full" disabled={isLoading || !acceptedTerms}>
                  {isLoading ? t('common.loading') : t('auth.signup')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-card px-2 text-muted-foreground">
                {t('auth.orContinueWith')}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('auth.signInWithGoogle')}
            </Button>

            {enableAppleLogin && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleAppleSignIn}
                disabled={isLoading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t('auth.signInWithApple')}
              </Button>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground text-center mt-4 leading-snug">
            {t('auth.oauthTermsNotice') ||
              'Ao continuar, você concorda com nossos'}{' '}
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {t('auth.termsOfUse') || 'Termos de Uso'}
            </a>{' '}
            {t('common.and') || 'e'}{' '}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {t('auth.privacyPolicy') || 'Política de Privacidade'}
            </a>
            .
          </p>
        </CardContent>

        <CardFooter className="justify-center">
          {!Capacitor.isNativePlatform() && (
            <Button variant="link" onClick={() => navigate('/')}>
              ← {t('common.back')}
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) { setForgotSent(false); setForgotEmail(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('auth.forgotPassword')}</DialogTitle>
            <DialogDescription>{t('auth.forgotPasswordDescription')}</DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="flex flex-col items-center py-4 gap-3">
              <CheckCircle className="w-12 h-12 text-primary" />
              <p className="text-center text-sm text-muted-foreground">{t('auth.forgotPasswordSent')}</p>
              <Button variant="outline" onClick={() => setShowForgotPassword(false)}>
                {t('common.back')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={forgotLoading}>
                {forgotLoading ? t('common.loading') : t('auth.sendResetLink')}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
